import React, { useEffect, useRef } from 'react';
import { useCopilot } from '../hooks/use-copilot';
import { MessageList } from './message-list';
import { MessageInput } from './message-input';
import { useLangGraph } from '../hooks/use-langgraph';
import { X, Maximize2, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SidebarProps {
  width?: number;
  customHeader?: JSX.Element;
  showCloseButton?: boolean;
  persistHistory?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  width = 500,
  customHeader,
  showCloseButton = true,
  persistHistory = true,
}) => {
  const { isOpen, closeSidebar } = useCopilot();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    sendMessage,
    stopGeneration,
    isLoading,
    error,
    clearHistory,
  } = useLangGraph({
    persistHistory,
    onError: (err) => {
      console.error('Error in LangGraph:', err);
    },
  });

  // Handle ESC key to close sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeSidebar]);

  // Focus the textarea when the sidebar opens
  useEffect(() => {
    if (isOpen) {
      // Use a longer delay to ensure the sidebar is fully rendered
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 500);
    }
  }, [isOpen]);

  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeSidebar]);

  // Default header component - Gemini style
  const defaultHeader: JSX.Element = (
    <div className="flex justify-between items-center px-3 py-3 bg-white">
      <div className="flex items-center">
        <div className="flex items-center justify-center w-6 h-6 ml-2">
          <Brain className="w-5 h-5 text-primary stroke-[1.5px]" />
        </div>
        <h2 className="text-base ml-2 font-medium text-black">AltoGPT</h2>
      </div>
      <div className="flex space-x-1">
        <button
          onClick={(e) => {
            e.preventDefault();
            // Clear history and create a new chat
            clearHistory();
            // No need to reload the page - the clearHistory function already resets the state
          }}
          className="text-gray-500 hover:text-primary hover:bg-blue-50/50 p-1.5 rounded-lg transition-all duration-200"
          aria-label="New chat"
          title="New chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14"></path>
          </svg>
        </button>
        <button
          onClick={() => { }}
          className="text-gray-500 hover:text-primary hover:bg-blue-50/50 p-1.5 rounded-lg transition-all duration-200"
          aria-label="Maximize"
          title="Maximize"
        >
          <Maximize2 className="w-5 h-5 stroke-[1.5px]" />
        </button>
        {showCloseButton && (
          <button
            onClick={closeSidebar}
            className="text-gray-500 hover:text-primary hover:bg-blue-50/50 p-1.5 rounded-lg transition-all duration-200"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 stroke-[1.5px]" />
          </button>
        )}
      </div>
    </div>
  );

  // Adjust width for mobile devices
  const responsiveWidth = typeof window !== 'undefined' && window.innerWidth < 640
    ? Math.min(width, window.innerWidth - 20)
    : width;

  return (
    <>
      {/* Backdrop - covers the entire screen */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-10 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
        onClick={closeSidebar} // Close when clicking backdrop
      />

      {/* Sidebar - appears from right side */}
      <div
        ref={sidebarRef}
        className={`fixed top-14 right-2 h-[calc(100vh-65px)] bg-white shadow-md z-50 flex flex-col transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-[calc(100%+8px)]'
          }`}
        style={{
          width: `${responsiveWidth}px`,
          maxWidth: 'calc(100vw - 48px)',
          borderRadius: '0.75rem',
          overflow: 'hidden',
        }}
        aria-label="Copilot sidebar"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        {customHeader as React.ReactNode || defaultHeader}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 text-red-600 border-b border-red-100 px-4 py-2 text-sm font-medium">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {typeof error === 'object' && error !== null && 'message' in error ? (error.message as React.ReactNode) : 'An error occurred'}
            </div>
          </div>
        )}

        {/* Messages */}
        <MessageList messages={messages} isLoading={isLoading} />

        {/* Input */}
        <MessageInput
          onSendMessage={sendMessage}
          isLoading={isLoading}
          onStopGeneration={stopGeneration}
        />
      </div>
    </>
  );
};
